import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, AlertCircle, ArrowLeft, ShieldCheck, LogIn, Sparkles } from 'lucide-react';
import { authService } from '../../Services/AuthService';
import { Logo } from '../ui/Logo';
import { useAuthModal } from '../../contexts/AuthModalContext';

const VerifyAccountView: React.FC = () => {
    const { token: routeToken } = useParams<{ token?: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { openAuthModal } = useAuthModal();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('Activation de votre compte en cours...');
    const hasRequestedRef = useRef<string | null>(null);

    useEffect(() => {
        const token = routeToken || searchParams.get('token');
        if (!token) {
            setStatus('error');
            setMessage('Le lien d\'activation est invalide ou incomplet.');
            return;
        }

        if (hasRequestedRef.current === token) {
            return;
        }
        hasRequestedRef.current = token;

        const verifyAccount = async () => {
            try {
                const response = await authService.verifyRegistration(token);
                if (response.success) {
                    setStatus('success');
                    setMessage(response.message || 'Votre compte a été activé avec succès ! Vous pouvez désormais vous connecter immédiatement.');
                } else {
                    setStatus('error');
                    setMessage(response.message || 'Le lien d\'activation est invalide ou a expiré (validité 24h).');
                }
            } catch (err: any) {
                setStatus('error');
                const serverMsg = err.message || err.data?.message || err.response?.data?.message || 'Une erreur est survenue lors de l\'activation de votre compte.';
                setMessage(serverMsg);
            }
        };

        verifyAccount();
    }, [routeToken, searchParams]);

    const handleLoginClick = () => {
        navigate('/');
        setTimeout(() => {
            openAuthModal();
        }, 150);
    };

    return (
        <div className="min-h-[85vh] flex items-center justify-center p-4 sm:p-6 bg-slate-50">
            <div className="max-w-md w-full bg-white rounded-3xl p-8 sm:p-10 shadow-xl border border-slate-200/80 text-center">
                {/* Header Logo */}
                <div className="flex justify-center mb-6">
                    <Logo variant="full" height={38} />
                </div>

                {status === 'loading' && (
                    <div className="py-8">
                        <div className="w-12 h-12 border-4 border-violet-100 border-t-violet-600 rounded-full animate-spin mx-auto mb-5"></div>
                        <h2 className="text-lg font-semibold text-slate-800 mb-2">Activation en cours</h2>
                        <p className="text-sm text-slate-500">{message}</p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="py-2">
                        <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600 border border-emerald-100 shadow-sm">
                            <CheckCircle2 className="w-9 h-9" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-900 mb-2">Compte Activé avec Succès !</h2>
                        
                        <p className="text-xs sm:text-sm text-slate-600 mb-5 leading-relaxed">
                            {message}
                        </p>

                        <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-100 rounded-2xl p-4 mb-6 text-left">
                            <div className="flex items-start gap-3">
                                <Sparkles className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-xs font-bold text-violet-900 uppercase tracking-wider mb-1">Accès Instantané Activé</p>
                                    <p className="text-xs text-slate-700 leading-relaxed">
                                        Votre compte est validé. Vous avez désormais un <strong>accès direct et immédiat</strong> à l'ensemble de la plateforme documentaire.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-center gap-2 text-xs text-emerald-700 font-medium mb-6">
                            <ShieldCheck className="w-4 h-4 text-emerald-600" />
                            <span>Votre adresse email est certifiée et vérifiée</span>
                        </div>

                        <div className="space-y-2.5">
                            <button 
                                onClick={handleLoginClick}
                                className="w-full py-3.5 px-5 bg-violet-700 hover:bg-violet-800 active:bg-violet-900 text-white font-bold text-sm rounded-xl transition-all shadow-md shadow-violet-700/20 flex items-center justify-center gap-2 cursor-pointer"
                            >
                                <LogIn className="w-4 h-4" />
                                <span>Se connecter maintenant</span>
                            </button>
                            <button 
                                onClick={() => navigate('/')}
                                className="w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-xs rounded-xl transition-colors cursor-pointer"
                            >
                                Retour à l'accueil
                            </button>
                        </div>
                    </div>
                )}

                {status === 'error' && (
                    <div className="py-2">
                        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600 border border-red-100">
                            <AlertCircle className="w-9 h-9" />
                        </div>
                        <h2 className="text-xl font-bold text-slate-900 mb-2">Lien non valide ou expiré</h2>
                        <p className="text-xs sm:text-sm text-slate-600 mb-6 leading-relaxed">
                            {message}
                        </p>

                        <div className="flex flex-col gap-2.5">
                            <Link 
                                to="/register"
                                className="w-full py-3 px-4 bg-violet-700 hover:bg-violet-800 text-white font-semibold text-xs rounded-xl transition-colors shadow-sm"
                            >
                                Renouveler mon inscription
                            </Link>
                            <button 
                                onClick={() => navigate('/')}
                                className="w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-xs rounded-xl transition-colors flex items-center justify-center gap-2 cursor-pointer"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Retour à l'accueil
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VerifyAccountView;
